// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Button from "../../../../../components/ui/Button";
import { saveHotInfoTrack, getHotInfoLanguages } from "../../../../../api";
import Dropdown from "../../../../../components/common/Dropdown";
import TitleSubtitle from "../../../../../components/common/TitleSubtitle";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import stepStyles from "../../createStep.module.css";
import partnerStyles from "./PartnerEdit.module.css";
import { useGlobalState } from "../../../../../context/useGlobalState";
import { HotInfoLanguageProps, RouteParams, DropdownOption } from "../../../../../types/screensTypes";

const HotInfoLanguage: React.FC<HotInfoLanguageProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { data, loading, error } = useGlobalState();
    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const { hotinfoId } = useParams<RouteParams>();
    const [existingLanguages, setExistingLanguages] = useState<string[]>([]);
    const [showAddLanguageDropdown, setShowAddLanguageDropdown] = useState<boolean>(false);
    const { t: rawT } = useTranslation();
    const t = rawT as any;

    const saveHotInfo = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;

        if (!hotinfoId || !trackId || !token || !selectedContentLanguage) {
            return;
        }

        const hotinforeturn = await saveHotInfoTrack({
            token,
            trackId,
            id: hotinfoId,
            language: selectedContentLanguage,
        });

        if (hotinforeturn) {
            const id = hotinforeturn.localizedId;
            navigateToNext(id, selectedContentLanguage);
        }
    };

    const getExistingLanguagesAPI = async (): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token || !hotinfoId) return;
        const languages = await getHotInfoLanguages({ token, hotinfoId });
        const languagesArray: string[] = languages?.map((lang: { id: string }) => lang.id) || [];
        setExistingLanguages(languagesArray);
    };

    useEffect(() => {
        if (hotinfoId && hotinfoId !== "") {
            getExistingLanguagesAPI();
        }
    }, [hotinfoId]);

    const languagesFiltered: DropdownOption[] = [
        ...(data.languagesAI?.filter((lang: DropdownOption) => existingLanguages.includes(lang.value)) || []),
        { label: t("addANewLanguage"), value: "add-new-language" },
    ];

    const languagesUnfiltered: DropdownOption[] =
        data.languagesAI?.filter((lang: DropdownOption) => !existingLanguages.includes(lang.value)) || [];

    const handleLanguageChange = (selectedOption: string): void => {
        if (selectedOption === "add-new-language") {
            setShowAddLanguageDropdown(true);
            setSelectedContentLanguage(null);
        } else {
            setShowAddLanguageDropdown(false);
            setSelectedContentLanguage(selectedOption);
        }
    };

    return (
        <div className={`${stepStyles.editScreen} ${partnerStyles.screen}`}>
            <TitleSubtitle title={t("selectTheLanguageOfYourPartner")} subtitle={""} layout="edit" />
            <h5>{t("existingLanguages")}</h5>
            <div className={partnerStyles.fieldNarrow}>
                <Dropdown
                    label=""
                    options={languagesFiltered}
                    onChange={handleLanguageChange}
                    loading={loading}
                    error={error ? true : false}
                    placeholder={t("language")}
                    value={selectedContentLanguage}
                />
            </div>
            {showAddLanguageDropdown && (
                <>
                    <h5>{t("selectANewLanguage")}</h5>
                    <div className={partnerStyles.fieldNarrow}>
                        <Dropdown
                            label=""
                            options={languagesUnfiltered}
                            onChange={setSelectedContentLanguage}
                            loading={loading}
                            error={error ? true : false}
                            placeholder={t("language")}
                            value={selectedContentLanguage}
                        />
                    </div>
                </>
            )}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${partnerStyles.continueBtn}`}
                    label={t("continue")}
                    onClick={saveHotInfo}
                />
            </div>
        </div>
    );
};

export default HotInfoLanguage;
